import { EventSubscription } from './event-subscription';
import { Observer, ObserverCallback, ObserverConfig } from './observer';

/**
 * Event model
 */
export class AsyncEventEmitter<TEvent> {
    /**
     * List of the observince methods
     */
    private observers = new Set<Observer<TEvent>>();

    /**
     * Observers sequences
     * Sequence is created when needed
     */
    private observersSequence?: Observer<TEvent>[];

    /**
     * Subscrbe to before-action event
     */
    subscribe(cb: ObserverCallback<TEvent>, config?: ObserverConfig) {
        const observer: Observer<TEvent> = {
            cb,
            priority: 100
        };
        if (config) {
            Object.assign(observer, config);
        }
        this.observers.add(observer);
        // invalidate existing sequence
        if (this.observersSequence) {
            this.observersSequence = undefined;
        }

        return new EventSubscription(observer, this.observers, () => this.unsubscribe(observer));
    }

    /**
     * Dispatch event
     */
    async dispatch(event: TEvent): Promise<void> {
        for (const observer of this.getSequence()) {
            const result = observer.cb(event);
            if (result instanceof Promise) {
                await result;
            }
        }
    }

    /**
     * Remove observer from the list
     */
    unsubscribe(observer: Observer<TEvent>) {
        if (this.observers.has(observer)) {
            this.observers.delete(observer);
            const sequence = this.observersSequence;
            if (sequence) {
                const index = sequence.indexOf(observer);
                if (index >= 0) {
                    sequence.splice(index, 1);
                }
            }
        }
    }

    /**
     * Regenerate sequence if needed and return it
     */
    private getSequence(): Observer<TEvent>[] {
        if (!this.observersSequence) {
            this.observersSequence = Array.from(this.observers).sort((a, b) => a.priority - b.priority);
        }
        return this.observersSequence!;
    }
}
